import api from 'services/api';
import requestDeduplicationService from 'services/requestDeduplicationService';

/**
 * Serviço para consolidar dados financeiros detalhados de múltiplas empresas
 */
class FinanceiroConsolidadoService {
  primeiraListaNaoVazia(...listas) {
    return listas.find((lista) => Array.isArray(lista) && lista.length > 0) || [];
  }

  /**
   * Buscar dados financeiros consolidados de múltiplas empresas
   * @param {Array} companyNames - Array com nomes das empresas
   * @param {Object} params - Parâmetros de data
   * @returns {Promise<Object>} Dados consolidados
   */
  async buscarDadosConsolidados(companyNames, params = {}) {
    try {
      // Buscar dados de cada empresa individualmente (paralelo)
      const promises = companyNames.map(companyName => 
        this.buscarDadosEmpresa(companyName, params.data_inicio, params.data_fim, params)
      );
      
      const resultados = await Promise.all(promises);
      
      // Consolidar dados principais primeiro
      const dadosConsolidados = this.consolidarDadosFinanceiros(resultados);
      
      // Buscar dados históricos de fluxo de caixa para o gráfico
      try {
        const dadosHistoricos = await this.buscarDadosHistoricosFluxoCaixa(companyNames);
        if (dadosHistoricos && dadosHistoricos.length > 0) {
          dadosConsolidados.fluxo_caixa_historico = dadosHistoricos;
        }
      } catch (error) {
        console.warn('⚠️ Erro ao carregar dados históricos:', error);
        // Continuar sem dados históricos
      }
      
      // Retornar dados principais imediatamente
      const resultadoInicial = {
        success: true,
        data: dadosConsolidados
      };
      
      
      return resultadoInicial;
      
    } catch (error) {
      console.error('❌ Erro ao buscar dados financeiros consolidados:', error);
      return {
        success: false,
        error: error.message || 'Erro ao buscar dados financeiros consolidados',
        data: null
      };
    }
  }

  /**
   * Buscar dados financeiros de uma empresa específica
   * @param {string} companyName - Nome da empresa
   * @param {string} dataInicio - Data de início
   * @param {string} dataFim - Data de fim
   * @returns {Promise<Object>} Dados da empresa
   */
  async buscarDadosEmpresa(companyName, dataInicio, dataFim, params = {}) {
    try {
      // Usar o mesmo endpoint que o useEmpresasFinanceiro usa para obter dados financeiros detalhados
      const response = await requestDeduplicationService.executeRequest(
        (url, params) => api.post(url, params),
        '/n8n/financeiro/dashboard-detalhado',
        {
          empresa: companyName,
          data_inicio: dataInicio,
          data_fim: dataFim,
          tipo_financeiro: params.tipo_financeiro || 'todos'
        },
        'POST',
        90000 // 90 segundos de timeout (alinhado com backend)
      );



      // Usar a mesma lógica do financeiroService (modo individual)
      // Verificar se a resposta tem a estrutura SWR esperada
      if (response.data && response.data.success && response.data.data) {
        // Verificar se há estrutura aninhada (response.data.data.data)
        let dadosFinais = response.data.data;
        if (response.data.data.data) {
          dadosFinais = response.data.data.data;
        }
        
        return { 
          companyName, 
          success: true, 
          data: dadosFinais
        };
      } else if (response.data && response.data.data) {
        // Estrutura direta sem success (fallback)
        // Verificar se há estrutura aninhada (response.data.data.data)
        let dadosFinais = response.data.data;
        if (response.data.data.data) {
          dadosFinais = response.data.data.data;
        }
        
        return { 
          companyName, 
          success: true, 
          data: dadosFinais
        };
      } else if (response.data && (response.data.kpis || response.data.fluxoCaixa)) {
        // Estrutura direta (dados brutos) - como MOV Fibra está retornando
        return { 
          companyName, 
          success: true, 
          data: response.data
        };
      } else {
        return { 
          companyName, 
          success: false, 
          data: null,
          error: 'Resposta inválida do servidor'
        };
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar dados financeiros da empresa ${companyName}:`, error);
      return { 
        companyName, 
        success: false, 
        data: null,
        error: error.message 
      };
    }
  }

  /**
   * Consolidar dados financeiros de múltiplas empresas
   * @param {Array} resultados - Array com resultados de cada empresa
   * @returns {Object} Dados consolidados
   */
  consolidarDadosFinanceiros(resultados) {
    // Análise de status das empresas
    const statusEmpresas = this.analisarStatusEmpresas(resultados);
    
    const dadosConsolidados = {
      // KPIs consolidados
      kpis: {
        receita_total: 0,
        despesas_total: 0,
        saldo_liquido: 0,
        margem_lucro: 0,
        ticket_medio: 0,
        total_clientes: 0,
        entradas: 0,
        entradas_brutas: 0,
        entradas_liquidas: 0,
        descontos: 0,
        impostos: 0,
        saidas: 0,
        variacao: 0,
        caixa_liquido: 0
      },
      
      // Dados por empresa
      empresas: [],
      
      // Dados consolidados por seção
      receita_cidade: [],
      analise_cidade: [],
      contas_pagamento: [],
      dre_resumo: [],
      dre_detalhada: [],
      
      // Status e validação das empresas
      status_empresas: statusEmpresas,
      empresas_com_dados: statusEmpresas.com_dados.length,
      empresas_sem_dados: statusEmpresas.sem_dados.length,
      empresas_com_erro: statusEmpresas.com_erro.length,
      
      // Metadados
      periodo: {
        inicio: resultados[0]?.data?.periodo?.inicio || new Date().toISOString().split('T')[0],
        fim: resultados[0]?.data?.periodo?.fim || new Date().toISOString().split('T')[0]
      },
      total_empresas: resultados.filter(r => r.success).length,
      timestamp: new Date().toISOString()
    };

    // Processar dados de cada empresa com validação
    resultados.forEach((resultado) => {
      if (resultado.success && resultado.data) {
        const data = resultado.data;
        
        // Validar se a empresa tem dados válidos
        const temDadosValidos = this.validarDadosEmpresa(data);
        
        // Adicionar empresa aos dados consolidados com status
        dadosConsolidados.empresas.push({
          nome: resultado.companyName,
          cor: this.getCompanyColor(resultado.companyName),
          dados: data,
          status: temDadosValidos ? 'com_dados' : 'sem_dados',
          tem_dados: temDadosValidos,
          kpis_validos: data.kpis ? this.validarKPIs(data.kpis) : false
        });


        if (data.kpis && temDadosValidos) {
          // Capturar campos com fallback seguro
          const entradas = parseFloat(data.kpis.entradas) || 0;
          const entradasBrutas = parseFloat(data.kpis.entradas_brutas ?? data.kpis.entradas) || 0;
          const descontos = parseFloat(data.kpis.descontos ?? 0) || 0;
          const impostos = parseFloat(data.kpis.impostos ?? 0) || 0;
          const entradasLiquidas = parseFloat(data.kpis.entradas_liquidas ?? (entradasBrutas - descontos - impostos)) || 0;
          const receita = parseFloat(data.kpis.receita_total ?? entradasBrutas) || 0;
          const despesas = parseFloat(data.kpis.despesas_total ?? data.kpis.saidas) || 0;
          const saidas = parseFloat(data.kpis.saidas ?? despesas) || 0;
          const caixaLiquido = parseFloat(data.kpis.caixa_liquido ?? data.kpis.saldo_liquido ?? (entradasLiquidas - saidas)) || 0;

          // Somatórios
          dadosConsolidados.kpis.receita_total += receita;
          dadosConsolidados.kpis.despesas_total += despesas;
          dadosConsolidados.kpis.entradas += entradas;
          dadosConsolidados.kpis.entradas_brutas += entradasBrutas;
          dadosConsolidados.kpis.entradas_liquidas += entradasLiquidas;
          dadosConsolidados.kpis.descontos += descontos;
          dadosConsolidados.kpis.impostos += impostos;
          dadosConsolidados.kpis.saidas += saidas;

          // Consolidar outros KPIs disponíveis
          if (data.kpis.saldo_liquido !== undefined || data.kpis.caixa_liquido !== undefined) {
            dadosConsolidados.kpis.saldo_liquido += parseFloat(data.kpis.saldo_liquido ?? caixaLiquido) || 0;
          }
          if (data.kpis.margem_lucro !== undefined) {
            dadosConsolidados.kpis.margem_lucro += parseFloat(data.kpis.margem_lucro) || 0;
          }
          if (data.kpis.total_clientes !== undefined) {
            dadosConsolidados.kpis.total_clientes += parseFloat(data.kpis.total_clientes) || 0;
          }
          // Mapear campo esperado na UI
          dadosConsolidados.kpis.caixa_liquido = (dadosConsolidados.kpis.caixa_liquido || 0) + caixaLiquido;
        }

        // Consolidar receita por cidade
        const dadosReceitaCidade = this.primeiraListaNaoVazia(
          data.dadosCidades,
          data.dadosCidade
        );

        if (dadosReceitaCidade.length > 0) {
          this.consolidarReceitaCidade(dadosConsolidados.receita_cidade, dadosReceitaCidade, resultado.companyName);
        } else {
          // Adicionar dados de cidade vazios para empresas sem dados
          dadosConsolidados.receita_cidade.push({
            cidade: `${resultado.companyName} (Sem dados)`,
            receita: 0,
            empresas: [{
              nome: resultado.companyName,
              receita: 0
            }]
          });
        }

        // Consolidar análise por cidade
        const dadosAnaliseCidade = this.primeiraListaNaoVazia(
          data.analise_cidade,
          data.analiseCidade,
          data.dadosCidade,
          data.dadosCidades
        );

        if (dadosAnaliseCidade.length > 0) {
          if (data.analise_cidade && Array.isArray(data.analise_cidade) && data.analise_cidade.length > 0) {
            this.consolidarAnaliseCidade(dadosConsolidados.analise_cidade, data.analise_cidade, resultado.companyName);
          } else {
            this.criarAnaliseCidadeAPartirDeDadosCidades(dadosConsolidados.analise_cidade, dadosAnaliseCidade, resultado.companyName);
          }
        }

        // Consolidar contas a pagar
        if (data.contasApagarDia && Array.isArray(data.contasApagarDia)) {
          this.consolidarContasPagamento(dadosConsolidados.contas_pagamento, data.contasApagarDia, resultado.companyName);
        }


        // Consolidar DRE
        if (data.dreResumo && Array.isArray(data.dreResumo)) {
          this.consolidarDREResumo(dadosConsolidados.dre_resumo, data.dreResumo, resultado.companyName);
        }

        if (data.dreDetalhada && Array.isArray(data.dreDetalhada)) {
          this.consolidarDREDetalhada(dadosConsolidados.dre_detalhada, data.dreDetalhada, resultado.companyName);
        }
      }
    });

    // Calcular KPIs finais
    dadosConsolidados.kpis.saldo_liquido = dadosConsolidados.kpis.receita_total - dadosConsolidados.kpis.despesas_total;
    // Ajustes finais para compatibilidade com a UI
    if (!dadosConsolidados.kpis.caixa_liquido) {
      dadosConsolidados.kpis.caixa_liquido = dadosConsolidados.kpis.saldo_liquido;
    }
    // Compatibilidade: manter 'entradas' alinhado às brutas quando possível
    if (!dadosConsolidados.kpis.entradas) {
      dadosConsolidados.kpis.entradas = dadosConsolidados.kpis.entradas_brutas || dadosConsolidados.kpis.receita_total;
    }
    if (!dadosConsolidados.kpis.entradas_brutas) {
      dadosConsolidados.kpis.entradas_brutas = dadosConsolidados.kpis.receita_total;
    }
    if (!dadosConsolidados.kpis.entradas_liquidas) {
      dadosConsolidados.kpis.entradas_liquidas = dadosConsolidados.kpis.entradas_brutas - (dadosConsolidados.kpis.descontos || 0) - (dadosConsolidados.kpis.impostos || 0);
    }
    dadosConsolidados.kpis.saidas = dadosConsolidados.kpis.despesas_total;
    
    
    
    if (dadosConsolidados.kpis.receita_total > 0) {
      dadosConsolidados.kpis.margem_lucro = (dadosConsolidados.kpis.saldo_liquido / dadosConsolidados.kpis.receita_total) * 100;
    }

    
    // Recalcular ticket médio se necessário
    if (dadosConsolidados.kpis.total_clientes > 0 && (!dadosConsolidados.kpis.ticket_medio || Number.isNaN(dadosConsolidados.kpis.ticket_medio))) {
      dadosConsolidados.kpis.ticket_medio = dadosConsolidados.kpis.receita_total / dadosConsolidados.kpis.total_clientes;
    }
    
    // Calcular participação para análise por cidade
    if (dadosConsolidados.analise_cidade && dadosConsolidados.analise_cidade.length > 0) {
      const valorTotalGeral = dadosConsolidados.analise_cidade.reduce((total, item) => total + (item.valor_total || 0), 0);
      dadosConsolidados.analise_cidade.forEach(item => {
        item.participacao = valorTotalGeral > 0 ? ((item.valor_total || 0) / valorTotalGeral) * 100 : 0;
      });
      
      // Ordenar por participação em ordem decrescente
      dadosConsolidados.analise_cidade.sort((a, b) => (b.participacao || 0) - (a.participacao || 0));
    }


    return dadosConsolidados;
  }

  /**
   * Consolidar receita por cidade
   */
  consolidarReceitaCidade(consolidado, dados, empresa) {
    dados.forEach(item => {
      const cidadeExistente = consolidado.find(c => c.cidade === item.cidade);
      const valorTotal = parseFloat(item.valor_total) || 0;
      
      if (cidadeExistente) {
        cidadeExistente.receita += valorTotal;
        cidadeExistente.empresas.push({
          nome: empresa,
          receita: valorTotal
        });
      } else {
        consolidado.push({
          cidade: item.cidade,
          receita: valorTotal,
          empresas: [{
            nome: empresa,
            receita: valorTotal
          }]
        });
      }
    });
  }

  /**
   * Criar análise por cidade a partir de dados de receita por cidade
   */
  criarAnaliseCidadeAPartirDeDadosCidades(consolidado, dadosCidades, empresa) {
    dadosCidades.forEach(item => {
      const cidadeExistente = consolidado.find(c => c.cidade === item.cidade);
      
      const valorTotal = parseFloat(item.valor_total) || 0;
      const valorRecebido = parseFloat(item.valor_recebido) || 0;
      const titulosTotal = parseInt(item.titulos_total) || 0;
      const titulosRecebidos = parseInt(item.titulos_recebidos) || 0;
      
      // Calcular métricas
      // Taxa de recebimento baseada em TÍTULOS ao invés de valores
      // Se não houver dados de títulos, usar valores como fallback
      const taxaRecebimento = titulosTotal > 0 
        ? (titulosRecebidos / titulosTotal) * 100 
        : (valorTotal > 0 ? (valorRecebido / valorTotal) * 100 : 0);
      const ticketMedio = titulosRecebidos > 0
        ? valorRecebido / titulosRecebidos
        : (titulosTotal > 0 ? valorTotal / titulosTotal : 0);
      
      // Determinar status baseado na taxa de recebimento (igual ao formato individual)
      let status = 'Pendente';
      let statusCor = 'text-[color:var(--status-warn)]';
      let statusBg = 'bg-[color:var(--surface-muted)]';
      
      if (taxaRecebimento >= 90) {
        status = 'Excelente';
        statusCor = 'text-[color:var(--status-good)]';
        statusBg = 'bg-[color:var(--surface-muted)]';
      } else if (taxaRecebimento >= 75) {
        status = 'Bom';
        statusCor = 'text-[color:var(--accent)]';
        statusBg = 'bg-[color:var(--surface-muted)]';
      } else if (taxaRecebimento >= 60) {
        status = 'Regular';
        statusCor = 'text-[color:var(--status-warn)]';
        statusBg = 'bg-[color:var(--surface-muted)]';
      } else if (taxaRecebimento >= 40) {
        status = 'Atenção';
        statusCor = 'text-[color:var(--status-bad)]';
        statusBg = 'bg-[color:var(--surface-muted)]';
      } else {
        status = 'Crítico';
        statusCor = 'text-[color:var(--status-bad)]';
        statusBg = 'bg-[color:var(--surface-muted)]';
      }
      
      if (cidadeExistente) {
        cidadeExistente.valor_total += valorTotal;
        cidadeExistente.valor_recebido += valorRecebido;
        cidadeExistente.titulos_total += titulosTotal;
        cidadeExistente.titulos_recebidos += titulosRecebidos;
        cidadeExistente.total_clientes += titulosTotal; // Usar títulos como proxy para clientes
        cidadeExistente.ticket_medio = cidadeExistente.titulos_recebidos > 0
          ? cidadeExistente.valor_recebido / cidadeExistente.titulos_recebidos
          : (cidadeExistente.titulos_total > 0
            ? cidadeExistente.valor_total / cidadeExistente.titulos_total
            : 0);
        // Taxa de recebimento baseada em TÍTULOS ao invés de valores
        cidadeExistente.taxa_recebimento = cidadeExistente.titulos_total > 0 
          ? (cidadeExistente.titulos_recebidos / cidadeExistente.titulos_total) * 100 
          : (cidadeExistente.valor_total > 0 
            ? (cidadeExistente.valor_recebido / cidadeExistente.valor_total) * 100 
            : 0);
        
        cidadeExistente.empresas.push({
          nome: empresa,
          valor_total: valorTotal,
          valor_recebido: valorRecebido,
          titulos_total: titulosTotal,
          titulos_recebidos: titulosRecebidos,
          total_clientes: titulosTotal,
          ticket_medio: ticketMedio,
          taxa_recebimento: taxaRecebimento,
          status: status,
          statusCor: statusCor,
          statusBg: statusBg
        });
      } else {
        consolidado.push({
          cidade: item.cidade,
          valor_total: valorTotal,
          valor_recebido: valorRecebido,
          titulos_total: titulosTotal,
          titulos_recebidos: titulosRecebidos,
          total_clientes: titulosTotal,
          ticket_medio: ticketMedio,
          taxa_recebimento: taxaRecebimento,
          status: status,
          statusCor: statusCor,
          statusBg: statusBg,
          participacao: 0, // Será calculado depois
          empresas: [{
            nome: empresa,
            valor_total: valorTotal,
            valor_recebido: valorRecebido,
            titulos_total: titulosTotal,
            titulos_recebidos: titulosRecebidos,
            total_clientes: titulosTotal,
            ticket_medio: ticketMedio,
            taxa_recebimento: taxaRecebimento,
            status: status,
            statusCor: statusCor,
            statusBg: statusBg
          }]
        });
      }
    });
  }

  /**
   * Consolidar análise por cidade
   */
  consolidarAnaliseCidade(consolidado, dados, empresa) {
    dados.forEach(item => {
      const cidadeExistente = consolidado.find(c => c.cidade === item.cidade);
      const valorTotal = parseFloat(item.valor_total) || 0;
      const valorRecebido = parseFloat(item.valor_recebido) || 0;
      const titulosTotal = parseInt(item.titulos_total || item.titulos_emitidos || 0) || 0;
      const titulosRecebidos = parseInt(item.titulos_recebidos || item.titulos_pagos || 0) || 0;
      const totalClientes = parseInt(item.total_clientes) || titulosTotal;
      const ticketMedio = titulosRecebidos > 0
        ? valorRecebido / titulosRecebidos
        : (titulosTotal > 0 ? valorTotal / titulosTotal : 0);
      if (cidadeExistente) {
        cidadeExistente.valor_total = (cidadeExistente.valor_total || 0) + valorTotal;
        cidadeExistente.valor_recebido = (cidadeExistente.valor_recebido || 0) + valorRecebido;
        cidadeExistente.titulos_total = (cidadeExistente.titulos_total || 0) + titulosTotal;
        cidadeExistente.titulos_recebidos = (cidadeExistente.titulos_recebidos || 0) + titulosRecebidos;
        cidadeExistente.total_clientes = (cidadeExistente.total_clientes || 0) + totalClientes;
        cidadeExistente.ticket_medio = cidadeExistente.titulos_recebidos > 0
          ? cidadeExistente.valor_recebido / cidadeExistente.titulos_recebidos
          : (cidadeExistente.titulos_total > 0
            ? cidadeExistente.valor_total / cidadeExistente.titulos_total
            : 0);
        cidadeExistente.empresas.push({
          nome: empresa,
          valor_total: valorTotal,
          valor_recebido: valorRecebido,
          titulos_total: titulosTotal,
          titulos_recebidos: titulosRecebidos,
          total_clientes: totalClientes,
          ticket_medio: ticketMedio
        });
      } else {
        consolidado.push({
          cidade: item.cidade,
          valor_total: valorTotal,
          valor_recebido: valorRecebido,
          titulos_total: titulosTotal,
          titulos_recebidos: titulosRecebidos,
          total_clientes: totalClientes,
          ticket_medio: ticketMedio,
          empresas: [{
            nome: empresa,
            valor_total: valorTotal,
            valor_recebido: valorRecebido,
            titulos_total: titulosTotal,
            titulos_recebidos: titulosRecebidos,
            total_clientes: totalClientes,
            ticket_medio: ticketMedio
          }]
        });
      }
    });
  }

  /**
   * Buscar dados históricos de fluxo de caixa (11 meses atrás)
   * Otimizado com cache para evitar requisições desnecessárias
   */
  async buscarDadosHistoricosFluxoCaixa(companyNames) {
    try {
      // Verificar cache primeiro
      const cacheKey = `historico_${companyNames.join('_')}_${Date.now().toString().slice(0, -3)}`; // Cache por minuto
      const cached = sessionStorage.getItem(cacheKey);
      
      if (cached) {
        return JSON.parse(cached);
      }
      
      // Buscar dados históricos de cada empresa individualmente
      const promises = companyNames.map(companyName => 
        this.buscarDadosHistoricosEmpresa(companyName)
      );
      
      const resultados = await Promise.all(promises);
      
      // Consolidar dados históricos por mês
      const consolidadoPorMes = {};
      
        resultados.forEach(resultado => {
          if (resultado.success && resultado.data && Array.isArray(resultado.data)) {
            resultado.data.forEach(item => {
              const mes = item.mes;
              if (!consolidadoPorMes[mes]) {
                consolidadoPorMes[mes] = {
                  mes: mes,
                  entradas_brutas: 0,
                  entradas_liquidas: 0,
                  saidas: 0,
                  saldo: 0,
                  empresas: []
                };
              }
              
              const entradas_brutas = parseFloat(item.entradas_brutas) || 0;
              const entradas_liquidas = parseFloat(item.entradas_liquidas) || 0;
              const saidas = parseFloat(item.saidas) || 0;
              const saldo = parseFloat(item.saldo) || 0;
              
              consolidadoPorMes[mes].entradas_brutas += entradas_brutas;
              consolidadoPorMes[mes].entradas_liquidas += entradas_liquidas;
              consolidadoPorMes[mes].saidas += saidas;
              consolidadoPorMes[mes].saldo += saldo;
              consolidadoPorMes[mes].empresas.push({
                nome: resultado.companyName,
                entradas_brutas: entradas_brutas,
                entradas_liquidas: entradas_liquidas,
                saidas: saidas,
                saldo: saldo
              });
            });
          }
        });
      
      // Converter para array e ordenar por mês
      const resultadoFinal = Object.values(consolidadoPorMes).sort((a, b) => a.mes.localeCompare(b.mes));
      
      // Armazenar no cache
      sessionStorage.setItem(cacheKey, JSON.stringify(resultadoFinal));
      
      return resultadoFinal;
      
    } catch (error) {
      console.error('❌ Erro ao buscar dados históricos de fluxo de caixa:', error);
      return [];
    }
  }

  /**
   * Buscar dados históricos de uma empresa específica
   */
  async buscarDadosHistoricosEmpresa(companyName) {
    try {
      // Usar deduplicação para evitar requisições duplicadas
      const response = await requestDeduplicationService.executeRequest(
        (url, params) => api.post(url, params),
        '/n8n/financeiro/dashboard-detalhado',
        {
          action: 'gerar_dashboard_detalhado',
          empresa: companyName,
          data_inicio: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
          data_fim: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
        },
        'POST',
        30000 // 30 segundos de timeout
      );

        if (response.data && response.data.success && response.data.data) {
          const data = response.data.data[0] || response.data.data;
          
          // Adicionar mês atual (dados mais recentes) ao histórico apenas se não existir
          if (data.data && data.data.fluxoCaixa && data.data.fluxoCaixa[0]) {
            const mesAtual = data.data.fluxoCaixa[0];
            const mesAtualExiste = data.fluxoCaixaHistorico.some(item => item.mes === mesAtual.mes);
            
            if (!mesAtualExiste) {
              data.fluxoCaixaHistorico.unshift(mesAtual);
            }
          }
                  
        // Verificar se há dados históricos
        if (data.fluxoCaixaHistorico && Array.isArray(data.fluxoCaixaHistorico)) {
          return {
            companyName,
            success: true,
            data: data.fluxoCaixaHistorico
          };
        } else {
          return {
            companyName,
            success: false,
            data: []
          };
        }
      } else {
        return {
          companyName,
          success: false,
          data: []
        };
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar dados históricos da empresa ${companyName}:`, error);
      return {
        companyName,
        success: false,
        data: []
      };
    }
  }

  /**
   * Consolidar contas a pagar
   */
  consolidarContasPagamento(consolidado, dados, empresa) {
    dados.forEach(item => {
      const dataExistente = consolidado.find(c => c.data === item.label);
      if (dataExistente) {
        dataExistente.valor += parseFloat(item.valor) || 0;
        dataExistente.empresas.push({
          nome: empresa,
          valor: parseFloat(item.valor) || 0
        });
      } else {
        consolidado.push({
          data: item.label,
          valor: parseFloat(item.valor) || 0,
          empresas: [{
            nome: empresa,
            valor: parseFloat(item.valor) || 0
          }]
        });
      }
    });
  }


  /**
   * Consolidar DRE resumo
   */
  consolidarDREResumo(consolidado, dados, empresa) {
    dados.forEach(item => {
      const valor = parseFloat(item.total) || parseFloat(item.valor) || 0;
      const tipoExistente = consolidado.find(c => c.tipo === item.tipo);
      if (tipoExistente) {
        tipoExistente.valor += valor;
        tipoExistente.empresas.push({
          nome: empresa,
          valor: valor
        });
      } else {
        consolidado.push({
          tipo: item.tipo,
          valor: valor,
          empresas: [{
            nome: empresa,
            valor: valor
          }]
        });
      }
    });
  }

  /**
   * Consolidar DRE detalhada
   */
  consolidarDREDetalhada(consolidado, dados, empresa) {
    dados.forEach(item => {
      const valor = parseFloat(item.total) || parseFloat(item.valor) || 0;
      const tipoExistente = consolidado.find(c => c.tipo === item.tipo);
      
      // Criar item detalhado com informações completas
      const itemDetalhado = {
          nome: empresa,
          valor: valor,
          descricao: item.planejamento || item.descricao || item.nome || 'Item',
          empresa_nome: empresa
      };
      
      if (tipoExistente) {
        tipoExistente.valor += valor;
        tipoExistente.empresas.push(itemDetalhado);
      } else {
        consolidado.push({
          tipo: item.tipo,
          valor: valor,
          empresas: [itemDetalhado]
        });
      }
    });
  }

  /**
   * Obter cor da empresa
   */
  getCompanyColor(companyName) {
    const cores = {
      'MOV Fibra': '#8B5CF6',
      'XT Fibra': '#DC2626',
      'Horizon Group': '#3B82F6'
    };
    return cores[companyName] || '#6B7280';
  }

  /**
   * Analisar status das empresas
   */
  analisarStatusEmpresas(resultados) {
    const com_dados = [];
    const sem_dados = [];
    const com_erro = [];

    resultados.forEach(resultado => {
      if (!resultado.success) {
        com_erro.push({
          nome: resultado.companyName,
          erro: resultado.error || 'Erro desconhecido',
          status: 'erro'
        });
      } else if (resultado.data && this.validarDadosEmpresa(resultado.data)) {
        com_dados.push({
          nome: resultado.companyName,
          status: 'com_dados',
          kpis: resultado.data.kpis
        });
      } else {
        sem_dados.push({
          nome: resultado.companyName,
          status: 'sem_dados',
          motivo: 'Dados zerados ou inválidos'
        });
      }
    });

    return {
      com_dados,
      sem_dados,
      com_erro,
      total: resultados.length,
      resumo: {
        com_dados: com_dados.length,
        sem_dados: sem_dados.length,
        com_erro: com_erro.length
      }
    };
  }

  /**
   * Validar se empresa tem dados válidos
   */
  validarDadosEmpresa(data) {
    if (!data || !data.kpis) {
      return false;
    }
    
    const kpis = data.kpis;
    const temEntradas = (parseFloat(kpis.entradas) || 0) > 0;
    const temEntradasLiquidas = (parseFloat(kpis.entradas_liquidas) || 0) > 0;
    const temSaidas = (parseFloat(kpis.saidas) || 0) > 0;
    const temCaixaLiquido = (parseFloat(kpis.caixa_liquido) || 0) > 0;
    
    // Considerar válido se pelo menos um KPI tem valor > 0
    return temEntradas || temEntradasLiquidas || temSaidas || temCaixaLiquido;
  }

  /**
   * Validar KPIs específicos
   */
  validarKPIs(kpis) {
    const campos = ['entradas', 'entradas_liquidas', 'saidas', 'caixa_liquido'];
    return campos.some(campo => (parseFloat(kpis[campo]) || 0) > 0);
  }
}

const financeiroConsolidadoService = new FinanceiroConsolidadoService();
export default financeiroConsolidadoService;
